import React, { useCallback, useLayoutEffect, useRef, useState, useEffect } from 'react';
import { gsap } from 'gsap';

const StaggeredProfileMenu = ({
  isOpen,
  onClose,
  children,
  colors = ['#0d9488', '#14b8a6'],
  accentColor = '#14b8a6',
  width,
}) => {
  const panelRef = useRef(null);
  const preLayersRef = useRef(null);
  const preLayerElsRef = useRef([]);
  const openTlRef = useRef(null);
  const closeTweenRef = useRef(null);
  const prevOpenRef = useRef(false);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      const panel = panelRef.current;
      const preContainer = preLayersRef.current;

      if (!panel) return;

      let preLayers = [];
      if (preContainer) {
        preLayers = Array.from(preContainer.querySelectorAll('.sm-prelayer'));
      }
      preLayerElsRef.current = preLayers;

      gsap.set([panel, ...preLayers], { xPercent: 100 });
    });
    return () => ctx.revert();
  }, []);

  const buildOpenTimeline = useCallback(() => {
    const panel = panelRef.current;
    const layers = preLayerElsRef.current;
    if (!panel) return null;

    openTlRef.current?.kill();
    closeTweenRef.current?.kill();

    const contentItems = Array.from(panel.querySelectorAll('.stagger-item'));
    
    const layerStates = layers.map(el => ({ el, start: Number(gsap.getProperty(el, 'xPercent')) }));
    const panelStart = Number(gsap.getProperty(panel, 'xPercent'));

    if (contentItems.length) gsap.set(contentItems, { yPercent: 30, opacity: 0 });

    const tl = gsap.timeline({ paused: true });

    layerStates.forEach((ls, i) => {
      tl.fromTo(ls.el, { xPercent: ls.start }, { xPercent: 0, duration: 0.4, ease: 'power4.out' }, i * 0.06);
    });

    const lastTime = layerStates.length ? (layerStates.length - 1) * 0.06 : 0;
    const panelInsertTime = lastTime + (layerStates.length ? 0.06 : 0);
    const panelDuration = 0.5;

    tl.fromTo(
      panel,
      { xPercent: panelStart },
      { xPercent: 0, duration: panelDuration, ease: 'power4.out' },
      panelInsertTime
    );

    if (contentItems.length) {
      const itemsStart = panelInsertTime + panelDuration * 0.2;
      tl.to(
        contentItems,
        { yPercent: 0, opacity: 1, duration: 0.6, ease: 'power3.out', stagger: { each: 0.05, from: 'start' } },
        itemsStart
      );
    }

    openTlRef.current = tl;
    return tl;
  }, []);

  const playOpen = useCallback(() => {
    const tl = buildOpenTimeline();
    if (tl) tl.play(0);
  }, [buildOpenTimeline]);

  const playClose = useCallback(() => {
    openTlRef.current?.kill();
    openTlRef.current = null;

    const panel = panelRef.current;
    const layers = preLayerElsRef.current;
    if (!panel) return;

    const all = [...layers, panel];
    closeTweenRef.current?.kill();

    closeTweenRef.current = gsap.to(all, {
      xPercent: 100,
      duration: 0.3,
      ease: 'power3.in',
      overwrite: 'auto',
      onComplete: () => {
        const contentItems = Array.from(panel.querySelectorAll('.stagger-item'));
        if (contentItems.length) gsap.set(contentItems, { yPercent: 30, opacity: 0 });
      }
    });
  }, []);

  useEffect(() => {
    if (isOpen && !prevOpenRef.current) {
      playOpen();
    } else if (!isOpen && prevOpenRef.current) {
      playClose();
    }
    prevOpenRef.current = isOpen;
  }, [isOpen, playOpen, playClose]);

  // Handle click outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event) => {
      if (
        panelRef.current &&
        !panelRef.current.contains(event.target) &&
        !event.target.closest('[data-profile-trigger]')
      ) {
        onClose?.();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose]);

  return (
    <div className="fixed inset-0 pointer-events-none z-[60]">
      {/* Pre-layers for staggered color effect */}
      <div
        ref={preLayersRef}
        className={`absolute top-0 right-0 bottom-0 pointer-events-none ${width ? '' : 'w-96'}`}
        style={width ? { width } : undefined}
        aria-hidden="true"
      >
        {colors.map((c, i) => (
          <div
            key={i}
            className="sm-prelayer absolute top-0 right-0 h-full w-full"
            style={{ background: c, opacity: 0.9 - i * 0.2 }}
          />
        ))}
      </div>

      {/* Main Panel */}
      <aside
        ref={panelRef}
        className={`absolute top-0 right-0 h-full bg-zinc-900/98 backdrop-blur-xl border-l border-zinc-700/50 shadow-2xl overflow-y-auto pointer-events-auto ${width ? '' : 'w-96'}`}
        style={{ 
          '--sm-accent': accentColor,
          WebkitBackdropFilter: 'blur(12px)',
          ...(width ? { width } : {}),
        }}
      >
        {children}
      </aside>
    </div>
  );
};

export default StaggeredProfileMenu;
